import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache, ServerError } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import { redirectToLogin } from 'app/business-logic/services/security-service/redirectToLogin';
import { store } from 'app/redux/store';
import { AppSettings } from 'core/AppSettings';
import { LogManager } from 'core/logging/LogManager';
import AuthParameters from './AuthParameters';
import possibleTypes from './global.possible-types.json';

const logger = LogManager.getLogger('ApolloProvider');

let globalClient: ApolloClient;

type Params = { companyAlias: string; facilityAlias: string };

export class ApolloProvider {
  public static global(params?: Params): ApolloClient {
    const link = createProviderLink(params);
    return (
      globalClient ||
      (globalClient = new ApolloClient({
        link,
        cache: new InMemoryCache({
          possibleTypes,
        }),
        defaultOptions: {
          query: {
            fetchPolicy: 'no-cache',
          },
        },
      }))
    );
  }
}

function getAuthDomain() {
  const { companyAlias, facilityAlias } = store.getState().profile.facility;
  if (!companyAlias || !facilityAlias) throw new Error('The facility is missing');
  return { companyAlias, facilityAlias };
}

const createAuthLink = (params?: Params) => {
  return new ApolloLink((operation, forward) => {
    const { companyAlias, facilityAlias } = params ?? getAuthDomain();
    const headers = AuthParameters.createRequestHeaders(companyAlias, facilityAlias);
    operation.setContext({ headers });
    return forward(operation);
  });
};

const createErrorLink = () => {
  return new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations }) =>
        logger.warn(`[GraphQL error]: Message: ${message}, Locations`, locations)
      );
    }
    if (ServerError.is(error) && error.statusCode === 401) {
      if (AppSettings.isCommunity) {
        return logger.error('Unauthorised request');
      }
      logger.error('Unauthorised request, redirecting to login page');
      return redirectToLogin();
    }
    if (error) {
      logger.error(`[Other error]: ${error}`);
    }
  });
};

const createHttpLink = () => {
  const uri: string = AppSettings.graphQLUrl;
  return new HttpLink({ uri, credentials: 'include' });
};

const createNamedLink = () => {
  const uri: string = AppSettings.graphQLUrl;
  return new ApolloLink((operation, forward) => {
    operation.setContext(() => ({
      uri: `${uri}?${operation.operationName}`,
    }));
    return forward(operation);
  });
};

export const createProviderLink = (params?: Params) => {
  const authLink = createAuthLink(params);
  const httpLink = createHttpLink();
  const errorLink = createErrorLink();
  const namedLink = createNamedLink();
  return ApolloLink.from([authLink, errorLink, namedLink, httpLink]);
};
